import { BasicButton, SecondCustomInput, Typography } from "@/components";
import { BackAppBar } from "@/components/ui";
import { STATUS } from "@/redux/constant";
import { verifyPassword } from "@/redux/reducers/auth";
import { AccountSettingChangePasswordFieldsSchema } from "@/schemas";
import { zodResolver } from "@hookform/resolvers/zod";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";

export const Route = createFileRoute(
	"/dashboard/accounts/settings/account/change-password",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const dispatch = useDispatch();
	const navigate = Route.useNavigate();
	const [passwordVisible, setPasswordVisible] = useState(false);
	const {
		control,
		setError,
		handleSubmit,
		formState: { isValid, isDirty, isSubmitting },
	} = useForm<AccountSettingChangePasswordFieldsSchema>({
		mode: "all",
		resolver: zodResolver(AccountSettingChangePasswordFieldsSchema),
	});

	const verifyPasswordState = useSelector(
		(state: any) => state.auth?.verifyPassword,
	);

	const onSubmit: SubmitHandler<AccountSettingChangePasswordFieldsSchema> = (
		data,
		e,
	) => {
		e?.stopPropagation();
		e?.preventDefault();
		return dispatch(
			verifyPassword({
				password: data.current_password,
				callback: () => {
					navigate({
						to: "/dashboard/accounts/settings/account/new-password",
					});
				},
			}),
		);
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (verifyPasswordState?.status === STATUS.FAILED) {
			setError("current_password", {
				type: "validate",
				message: "The password you entered is incorrect",
			});
		}
	}, [verifyPasswordState?.status]);

	return (
		<div className="wrapper !p-0 min-h-dvh mx-auto">
			<BackAppBar />
			<form onSubmit={handleSubmit(onSubmit)} className="container">
				<Stack spacing={4}>
					<Typography fontWeight={600} fontSize={28}>
						Enter current password
					</Typography>
					<Typography fontWeight={400} fontSize={14}>
						To keep your account save, please verify your identity by entering
						your current password.
					</Typography>
					<Controller
						control={control}
						name="current_password"
						defaultValue=""
						render={({ field, fieldState }) => (
							<SecondCustomInput
								type="password"
								label="Password"
								name="password"
								value={field.value}
								onChange={field.onChange}
								onBlur={field.onBlur}
								onToggleVisibility={() => setPasswordVisible(!passwordVisible)}
								showPassword={passwordVisible}
								startIcon={<LockOutlinedIcon />}
								errorMessage={fieldState.error?.message}
								isShowErrorMessage={!!fieldState.error?.message}
							/>
						)}
					/>
				</Stack>
				<Box className="fixed bg-white shadow bottom-0 inset-x-0 z-50 py-3">
					<Box className="wrapper mx-auto">
						<BasicButton
							type="submit"
							variant="contained"
							color="secondary"
							className="w-full !h-12 text-sm font-semibold !shadow-none"
							disabled={!isDirty || !isValid || isSubmitting}
							loading={isSubmitting}
						>
							Next
						</BasicButton>
					</Box>
				</Box>
			</form>
		</div>
	);
}
